import { Textarea, Tooltip } from '@nextui-org/react';
import styled from 'styled-components';

import { ChatIcon } from '../../assets';
import { Chevron2Icon } from '../../assets';
import { VehicleChatPlaceholder } from '../../assets';
import ZIndexLayers from '../../services/ZIndexLayers';
import { ThemeProp } from '../../theme/Themes';
import Avatar, { AvatarVariant } from '../Avatar';
import Button from '../Button';
import ChatMessageBubble from '../ChatMessageBubble';
import InfinitelyScrolled from '../InfinitelyScrolled';
import Text from '../Text';

const borderRadius = 'border-radius: 0.5rem 0.5rem 0rem 0rem';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 90vh;
  background: ${(props: ThemeProp) => props.theme.colors.primary.c900};
  ${borderRadius};

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    height: 100%;
    border-radius: 0rem;
  }
`;

interface ChatHeaderProps {
  $isExpanded?: boolean;
}
export const ChatHeader = styled.div<ChatHeaderProps>`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.875rem 1.25rem 0.625rem 1.5rem;
  height: 4.563rem;
  ${borderRadius};
  border-bottom: ${(props) => (props.$isExpanded ? 'none' : `1px solid ${props.theme.colors.greyscale.c800}`)};

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    border-radius: 0rem;
  }
`;

export const ExpandedChatHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 2.1rem;
  padding-left: 1.438rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid ${(props: ThemeProp) => props.theme.colors.greyscale.c800};
`;

export const ExpandedChatHeaderMembers = styled.div`
  display: flex;
  margin-right: 0.563rem;
`;

export const ExpandedChatHeaderMember = styled(Avatar)`
  width: 1.25rem;
  min-width: 1.25rem;
  height: 1.25rem;
  min-height: 1.25rem;
  border-radius: 2.13rem;

  // skips the first one
  :nth-of-type(n + 2) {
    margin-left: -0.25rem;
  }

  .nextui-avatar-text {
    -webkit-text-stroke: 0;
    font-size: 0.75rem;
    color: ${(props: ThemeProp) => props.theme.colors.greyscale.c800};
  }

  .nextui-avatar-bg {
    opacity: 1;
    background: ${(props: ThemeProp) => props.theme.colors.primary.c300};
  }
`;

export const ExpandedChatHeaderAllMembers = styled(ExpandedChatHeaderMember).attrs((props: ThemeProp) => {
  return {
    fontColor: props.theme.colors.primary.c700,
  };
})`
  width: 2rem;
  height: 2rem;
  margin-bottom: 0.375rem;
  .nextui-avatar-text {
    -webkit-text-stroke: 0;
    font-size: 0.75rem;
  }
`;

export const ExpandedChatHeaderMembersCount = styled(Text)`
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.lineHeight};
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
  cursor: pointer;
`;

export const ExpandedChatHeaderOnlineMembersCount = styled(Text)`
  margin-left: 0.25rem;
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.lineHeight};
  color: ${(props: ThemeProp) => props.theme.colors.system.success};
`;

export const VehicleChatIconContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  position: relative;
  min-width: 4.2rem;
  max-width: 4.2rem;
  padding: 1rem 0;
`; // Includes vehicle image + (user image + status)

export const VehicleIcon = styled.img`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 2.5rem;
  background-color: lightgray;
  object-fit: cover;
`;
export const MemberIconContainer = styled.div`
  display: flex;
  position: absolute;
  left: 2rem;
`; // Includes user image + status

export const MemberIcon = styled(Avatar).attrs(() => ({
  variant: AvatarVariant.LARGE,
}))`
  position: relative;
  box-sizing: content-box;

  .nextui-avatar-text {
    font-size: 1.25rem;
  }
`;

export const MemberStatusIcon = styled.div`
  width: 0.5rem;
  height: 0.5rem;
  position: absolute;
  bottom: -0.063rem;
  right: -0.125rem;
  box-sizing: content-box;
  z-index: ${ZIndexLayers.overlay};
  border-radius: 2.75rem;
  border: 1.5px solid ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
  background: ${(props: ThemeProp) => props.theme.colors.system.success};
`;

export const MemberChatIconContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  position: relative;
`; // Includes user image and status for Member header

export const GroupChatIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 1rem;
  background-color: ${(props: ThemeProp) => props.theme.colors.primary.c200};
`; // Includes group image

export const ChatHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  overflow: hidden;
  width: 100%;

  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    max-width: 60%;
  }
`;

export const GroupChatHeaderInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.313rem;
`;

export const ChatHeaderInfoTitle = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body1.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body1.lineHeight};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const ChatHeaderInfoSubTitle = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.lineHeight};
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
`;
interface GroupChatHeaderIconContainerProps {
  $isExpanded?: boolean;
}
export const GroupChatHeaderIconContainer = styled.div<GroupChatHeaderIconContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  transform: ${(props) => (props.$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s;
  cursor: pointer;
`;

export const CloseButtonContainer = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  cursor: pointer;

  svg {
    stroke: ${(props: ThemeProp) => props.theme.colors.white};
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    svg {
      stroke: ${(props: ThemeProp) => props.theme.colors.primary.c300};
    }
  }
`;

interface ChatBodyProps {
  $hasMoreMessages?: boolean;
  $hideScroll?: boolean;
}
export const ChatBody = styled.div<ChatBodyProps>`
  display: block;
  height: 100%;
  width: 100%;
  overflow-y: scroll;
  position: relative;
  // Hide top padding when there are more messages to load, so the loader can appear correctly.
  padding: ${(props) => (props.$hasMoreMessages ? '0 1.25rem 1.25rem 1.25rem' : '1.25rem')};
  overflow: ${(props) => (props.$hideScroll ? 'hidden' : 'auto')};

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    background: ${(props: ThemeProp) => props.theme.colors.primary.c900};
  }
`;

export const ChatMessagesContainer = styled.div<any>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${(props) => (props.centerContent ? 'center' : 'flex-end')};
  flex: 1;
  margin: 1rem 1.5rem;
  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
`;

export const ChatBodyEmptyPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.125rem;
  max-width: 20.125rem;
  text-align: center;
`;

export const ChatBodyEmptyPlaceholderTitle = styled(Text)`
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body1.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body1.lineHeight};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
`;

export const ChatBodyEmptyPlaceholderSubtitle = styled(Text)`
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body2.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body2.lineHeight};
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c100};
  max-width: 15rem;
`;

export const ChatInputBox = styled.div`
  display: flex;
  flex-direction: column-reverse;
  justify-content: space-between;

  margin: 0.75rem;
  margin-top: 0;
  background: ${(props: ThemeProp) => props.theme.colors.greyscale.c900};

  border: 1px solid ${(props: ThemeProp) => props.theme.colors.greyscale.c800};
  border-radius: 0.25rem;
`;

export const ChatImagePreviewContainer = styled.div`
  display: flex;
  width: 4rem;
  align-items: center;
  position: relative;

  #close-icon {
    width: 0.875rem;
    height: 0.875rem;
    box-sizing: content-box;
    border-radius: 1.25rem;
    border: 0.7px solid ${(props: ThemeProp) => props.theme.colors.black};
    stroke: ${(props: ThemeProp) => props.theme.colors.white};
    background: ${(props: ThemeProp) => props.theme.colors.black};
    cursor: pointer;

    position: absolute;
    top: 0.125rem;
    right: 0.563rem;
  }
`;

export const ChatImageLoading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  width: 2.5rem;
  height: 2.5rem;
  margin: 0.5rem;
  border-radius: 0.25rem;
  background: ${(props: ThemeProp) => props.theme.colors.greyscale.c300};
  position: relative;

  svg {
    position: absolute;
    top: 0.625rem;
  }
`;

export const ChatImageProgressBar = styled.div`
  width: 2.25rem;
  height: 0.25rem;
  display: flex;

  background: ${(props: ThemeProp) => props.theme.colors.white};
  border-radius: 0.188rem;
  margin: 0.188rem 0.125rem;
  padding: 0.063rem;
`;

interface ChatImageProgressBarFilledProps {
  percentage: number;
}
export const ChatImageProgressBarFilled = styled.div<ChatImageProgressBarFilledProps>`
  border-radius: 0.188rem;
  background: ${(props: ThemeProp) => props.theme.colors.primary.c600};
  width: ${(props) => props.percentage}%;
`;

export const ChatImagePreview = styled.img`
  position: relative;
  width: 2.5rem;
  height: 2.5rem;
  margin: 0.5rem;
  border-radius: 0.25rem;
  object-fit: cover;

  background: ${(props: ThemeProp) => props.theme.colors.greyscale.c300};
`;

export const ChatInputButtons = styled.div`
  display: flex;
  height: 1.75rem;
  margin: 0.5rem;
  gap: 0.5rem;
`;

export const ActionButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 100%;
  border-radius: 0.25rem;
  background: transparent;
  cursor: pointer;

  svg {
    width: 1.25rem;
    height: 1.25rem;
    stroke: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
  }
`;

export const SendButton = styled(Button)`
  margin-left: auto;
  min-width: 4.25rem;
  width: 4.25rem;
  height: 100%;
  padding: 0.25rem 1.063rem;
`;

export const StyledTextarea = styled(Textarea).attrs((props: ThemeProp) => ({
  css: {
    borderRadius: '0.25rem',
    textarea: {
      background: props.theme.colors.greyscale.c900,
      borderRadius: '0.25rem',
      margin: '0 !important',
      padding: '0.5rem 0.75rem !important',
    },
  },
}))``;

export const StyledMessageBubble = styled(ChatMessageBubble)`
  margin-bottom: 1rem;
`;

export const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const EmptyChatView = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

export const StyledInfiniteScroll = styled(InfinitelyScrolled)`
  width: 100%;
  flex-direction: column;
  align-self: flex-end;
  display: inline-flex;
  align-self: flex-end;
  height: 100%;
  justify-content: flex-end;
`;

/**
 * Expanded Group Header
 */
export const ExpandedChatHeaderAllMembersContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const ExpandedChatHeaderAllMembersText = styled.p`
  margin-left: 0.5rem;
  font-size: ${(props) => props.theme.fontSize.s};
  line-height: ${(props) => props.theme.lineHeight.m};
  font-weight: ${(props) => props.theme.fontWeight.regular};
`;

export const AllMembersListContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: baseline;
  padding: 0.5rem;
  min-width: 11.25rem;
  max-height: 21.875rem;
  overflow: scroll;
`;

export const MembersTooltip = styled(Tooltip).attrs((props) => ({
  css: {
    backgroundColor: `${props.theme.colors.white}`,
    border: `solid 1px ${props.theme.colors.greyscale.c200}`,
    minWidth: '11.25rem',
    zIndex: 999999,
  },
}))``;

export const StyledChatIcon = styled(ChatIcon).attrs((props) => {
  return {
    color: props.theme.colors.primary.c700,
  };
})``;

export const StyledChevronIcon = styled(Chevron2Icon)`
  stroke: ${(props) => props.theme.colors.white};
`;

export const StyledVehicleChatPlaceholder = styled(VehicleChatPlaceholder)`
  width: 2.5rem;
  height: 2.5rem;
`;

export const BackButtonContainer = styled.div`
  display: none;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  svg {
    rotate: 90deg;
    path {
      stroke: ${(props: ThemeProp) => props.theme.colors.primary.c050};
    }
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: flex;
  }
`;

export const ConversationTopViewContainer = styled.div`
  background: ${(props: ThemeProp) => props.theme.colors.greyscale.c800};
  padding: 0.75rem 1.5rem;
`;

export const ConversationDateDivider = styled.div`
  display: flex;
  padding: 0.375rem 1rem;
  margin-bottom: 1rem;
  align-self: center;
  border: 1px solid ${(props) => props.theme.colors.greyscale.c300};
  color: ${(props) => props.theme.colors.greyscale.c300};
  /* Default */
  box-shadow: 0px 2px 4px rgba(3, 8, 21, 0.04); // NOTE: Spec from design system.
  border-radius: 2rem;
  font-weight: ${(props) => props.theme.fontWeight.medium};
  font-size: 0.69rem;
  line-height: 1rem;
  text-transform: uppercase;
  z-index: ${ZIndexLayers.fixedElement};
  position: sticky;
  top: 0px;
  align-self: center;
`;

export const MessagesDateBlock = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
  justify-content: flex-end;
`;
